import { Injectable } from "@angular/core";
import { Observable, BehaviorSubject } from "rxjs";
import { ApiService, IPagination } from "./api.service";
import { tap } from "rxjs/operators";

export const USER_ENDPOINT = {
  getUserProfile: "users/profile",
  updateUserProfile: "users/me",
  changePassword: "users/change-password",
  logout: "auth/logout",
  deactivateAccount: "users/request-deactivation",
  verifyDeactivationOTP: "users/confirm-deactivation",
  getUsers: "users",
  updateCredits: "users/{userId}/credits",
  updateUser: "users/{userId}",
};

export type CreditPayload = {
  amount: number;
  transactionType: 'credit' | 'debit';
}

export type ApiConfigPayload = {
  mca?: boolean;
  legalCheck?: boolean;
  socialMediaAnalysis?: boolean;
}

@Injectable({
  providedIn: "root",
})
export class UserService {
  private USER_KEY = "user";
  // BehaviorSubject for user credits to reflect real-time changes
  private userCreditsSubject = new BehaviorSubject<number>(0);
  public userCredits$ = this.userCreditsSubject.asObservable();

  constructor(private apiService: ApiService) {
    this.initializeCreditsFromStorage();
  }

  private initializeCreditsFromStorage(): void {
    try {
      const userData = this.getUserDetails();
      if (userData) {
        const credits = userData?.credits ?? 0;
        this.userCreditsSubject.next(credits);
      }
    } catch (error) {
      console.error("Error initializing credits from storage:", error);
    }
  }

  getUserProfile(): Observable<any> {
    return this.apiService.get(USER_ENDPOINT.getUserProfile).pipe(
      tap((res: any) => {
        if (res.data) {
          this.setUserDetails(res.data?.user);
        }
      })
    );
  }

  updateUserProfile(data: any): Observable<any> {
    return this.apiService.patch(USER_ENDPOINT.updateUserProfile, data);
  }

  changePassword(data: any): Observable<any> {
    return this.apiService.patch(USER_ENDPOINT.changePassword, data);
  }

  requestAccountDeactivation(): Observable<any> {
    return this.apiService.get(USER_ENDPOINT.deactivateAccount);
  }

  confirmAccountDeactivation(otp: string, txnId: string): Observable<any> {
    return this.apiService.post(USER_ENDPOINT.verifyDeactivationOTP, {
      otp,
      txnId,
    });
  }

  setUserDetails(user: any): void {
    try {
      sessionStorage.setItem(this.USER_KEY, JSON.stringify(user));
      // Update credits subject so UI reflects immediately
      const credits = user?.credits ?? 0;
      this.userCreditsSubject.next(credits);
    } catch (error) {
      console.error("Error updating user store:", error);
    }
  }

  getUserDetails(): any {
    const user = JSON.parse(sessionStorage.getItem(this.USER_KEY) || "{}");
    return 'user' in user ? user.user : user;
  }

  updateUserCredits(credits: number): void {
    const user = this.getUserDetails();
    user.credits = credits;
    this.setUserDetails(user);
  }

  getCurrentCredits(): number {
    return this.userCreditsSubject.value;
  }

  getUsers({
    page, limit, filter = {}
  }: IPagination & { filter: Record<string, any>; } = {
      page: 1, limit: 10, filter: {}
    }): Observable<any> {
    const params = {
      page,
      limit,
      ...filter,
    };
    return this.apiService.get(USER_ENDPOINT.getUsers, { params });
  }

  updateCredits(userId: string, payload: CreditPayload): Observable<any> {
    const endpoint = USER_ENDPOINT.updateCredits.replace('{userId}', userId);
    return this.apiService.post(endpoint, payload, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  updateApiConfig(userId: string, apiConfig: ApiConfigPayload): Observable<any> {
    const endpoint = USER_ENDPOINT.updateUser.replace('{userId}', userId);
    return this.apiService.patch(endpoint, { apiConfig });
  }
}
